/*!
 * TKO conditional (if/ifnot/unless/with/else) bindings 🥊  @tko/binding.if@4.0.0-alpha8
 * (c) The Knockout.js Team - https://tko.io
 * License: MIT (http://www.opensource.org/licenses/mit-license.php)
 */

import { cloneNodes, virtualElements, cleanNode, domData, options } from '@tko/utils';
import { dependencyDetection, observable, unwrap } from '@tko/observable';
import { applyBindingsToDescendants, AsyncBindingHandler } from '@tko/bind';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */
/* global Reflect, Promise */

var extendStatics = Object.setPrototypeOf ||
    ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
    function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };

function __extends(d, b) {
    extendStatics(d, b);
    function __() { this.constructor = d; }
    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}

function __awaiter(thisArg, _arguments, P, generator) {
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : new P(function (resolve) { resolve(result.value); }).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function __generator(thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
}

function __read(o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
}

function __spread() {
    for (var ar = [], i = 0; i < arguments.length; i++)
        ar = ar.concat(__read(arguments[i]));
    return ar;
}

/**
 * Create a DOMbinding that controls DOM nodes presence
 *
 * Covers e.g.
 *
 * 1. DOM Nodes contents
 *
 * <div data-bind='if: x'>
 * <!-- else --> ... an optional 'if'
 * </div>
 *
 * 2. Virtual elements
 *
 * <!-- ko if: x -->
 * <!-- else -->
 * <!-- /ko -->
 *
 * 3. Else binding
 * <div data-bind='if: x'></div>
 * <div data-bind='else'></div>
 *
 * Requires `renderStatus` and `get bindingContext` to be overloaded,
 * and this.computed('render') must be called in the child constructor.
 */
var ConditionalBindingHandler = /** @class */ (function (_super) {
    __extends(ConditionalBindingHandler, _super);
    function ConditionalBindingHandler(params) {
        var _this = _super.call(this, params) || this;
        _this.hasElse = _this.detectElse(_this.$element);
        var elseChainSatisfied = _this.completesElseChain = observable();
        domData.set(_this.$element, 'conditional', { elseChainSatisfied: elseChainSatisfied });
        return _this;
    }
    ConditionalBindingHandler.prototype.getIfElseNodes = function () {
        if (this.ifElseNodes) {
            return this.ifElseNodes;
        }
        if (dependencyDetection.getDependenciesCount() || this.hasElse) {
            return this.cloneIfElseNodes(this.$element, this.hasElse);
        }
    };
    ConditionalBindingHandler.prototype.render = function () {
        var isFirstRender = !this.ifElseNodes;
        var shouldDisplay = this.renderStatus().shouldDisplay;
        // Save the nodes before we possibly remove them from the DOM.
        this.ifElseNodes = this.getIfElseNodes() || {};
        if (shouldDisplay) {
            var useOriginalNodes = isFirstRender && !this.hasElse;
            this.renderAndApplyBindings(this.ifElseNodes.ifNodes, useOriginalNodes);
        }
        else if (this.hasElse) {
            this.renderAndApplyBindings(this.ifElseNodes.elseNodes);
        }
        else {
            virtualElements.emptyNode(this.$element);
        }
    };
    ConditionalBindingHandler.prototype.renderAndApplyBindings = function (nodes, useOriginalNodes) {
        return __awaiter(this, void 0, void 0, function () {
            var bound;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!useOriginalNodes) {
                            virtualElements.setDomNodeChildren(this.$element, cloneNodes(nodes));
                        }
                        return [4 /*yield*/, applyBindingsToDescendants(this.bindingContext, this.$element)];
                    case 1:
                        bound = _a.sent();
                        this.completeBinding(bound);
                        return [2 /*return*/];
                }
            });
        });
    };
    Object.defineProperty(ConditionalBindingHandler.prototype, "elseChainIsAlreadySatisfied", {
        /**
         * This may be truthy for the `else` binding.
         */
        get: function () { return false; },
        enumerable: true,
        configurable: true
    });
    /**
     * Test a node for whether it represents an 'else' condition.
     * @param  {HTMLElement}  node to be tested
     * @return {Boolean}      true when
     *
     * Matches <!-- else -->
     */
    ConditionalBindingHandler.prototype.isElseNode = function (node) {
        return node.nodeType === 8 &&
            node.nodeValue.trim().toLowerCase() === 'else';
    };
    ConditionalBindingHandler.prototype.detectElse = function (element) {
        var children = virtualElements.childNodes(element);
        for (var i = 0, j = children.length; i < j; ++i) {
            if (this.isElseNode(children[i])) {
                return true;
            }
        }
        return false;
    };
    /**
     * Clone the nodes, returning `ifNodes`, `elseNodes`
     * @param  {HTMLElement} element The nodes to be cloned
     * @param  {boolean}    hasElse short-circuit to speed up the inner-loop.
     * @return {object}         Containing the cloned nodes.
     */
    ConditionalBindingHandler.prototype.cloneIfElseNodes = function (element, hasElse) {
        var children = virtualElements.childNodes(element);
        var ifNodes = [];
        var elseNodes = [];
        var target = ifNodes;
        for (var i = 0, j = children.length; i < j; ++i) {
            if (hasElse && this.isElseNode(children[i])) {
                target = elseNodes;
                hasElse = false;
            }
            else {
                target.push(cleanNode(children[i].cloneNode(true)));
            }
        }
        return { ifNodes: ifNodes, elseNodes: elseNodes };
    };
    Object.defineProperty(ConditionalBindingHandler.prototype, "controlsDescendants", {
        get: function () { return true; },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(ConditionalBindingHandler, "allowVirtualElements", {
        get: function () { return true; },
        enumerable: true,
        configurable: true
    });
    return ConditionalBindingHandler;
}(AsyncBindingHandler));

/**
 * For the `if:` binding.
 */
var IfBindingHandler = /** @class */ (function (_super) {
    __extends(IfBindingHandler, _super);
    function IfBindingHandler() {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        var _this = _super.apply(this, __spread(args)) || this;
        _this.ifCondition = _this.computed(function () { return !!unwrap(_this.value); });
        _this.computed('render');
        return _this;
    }
    IfBindingHandler.prototype.shouldDisplayIf = function () {
        return this.ifCondition();
    };
    Object.defineProperty(IfBindingHandler.prototype, "bindingContext", {
        get: function () {
            var _this = this;
            return this.ifCondition.isActive()
                ? this.$context.extend(function () {
                    // Ensure that this context is dependant upon the conditional, so the
                    // order of binding application is: conditional before its children.
                    // See https://github.com/knockout/kn
                    // ockout/pull/2226
                    _this.ifCondition();
                    return null;
                })
                : this.$context;
        },
        enumerable: true,
        configurable: true
    });
    IfBindingHandler.prototype.renderStatus = function () {
        var shouldDisplay = this.shouldDisplayIf();
        if (this.elseChainIsAlreadySatisfied) {
            shouldDisplay = false;
            // needsRefresh = isFirstRender || this.didDisplayOnLastUpdate FIXME
            this.completesElseChain(true);
        }
        else {
            this.completesElseChain(shouldDisplay);
        }
        return { shouldDisplay: shouldDisplay };
    };
    return IfBindingHandler;
}(ConditionalBindingHandler));
var UnlessBindingHandler = /** @class */ (function (_super) {
    __extends(UnlessBindingHandler, _super);
    function UnlessBindingHandler() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    UnlessBindingHandler.prototype.shouldDisplayIf = function () { return !_super.prototype.shouldDisplayIf.call(this); };
    return UnlessBindingHandler;
}(IfBindingHandler));

/**
 * The following fails somewhere in the `limit` functions of Observables i.e.
 * it's an issue related to async/deferUpdates.
 */
var WithBindingHandler = /** @class */ (function (_super) {
    __extends(WithBindingHandler, _super);
    function WithBindingHandler() {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        var _this = _super.apply(this, __spread(args)) || this;
        _this.asOption = _this.allBindings.get('as');
        // If given `as`, reduce the condition to a boolean, so it does not
        // change & refresh when the value is updated.
        var conditionalFn = _this.asOption && !options.createChildContextWithAs
            ? function () { return Boolean(unwrap(_this.value)); } : function () { return unwrap(_this.value); };
        _this.conditional = _this.computed(conditionalFn);
        _this.computed('render');
        return _this;
    }
    Object.defineProperty(WithBindingHandler.prototype, "bindingContext", {
        get: function () {
            var _a;
            if (!this.asOption) {
                return this.$context.createChildContext(this.valueAccessor);
            }
            return options.createChildContextWithAs
                ? this.$context.createChildContext(this.value, this.asOption)
                : this.$context.extend((_a = {}, _a[this.asOption] = this.value, _a));
        },
        enumerable: true,
        configurable: true
    });
    WithBindingHandler.prototype.renderStatus = function () {
        var shouldDisplay = Boolean(this.conditional());
        return { shouldDisplay: shouldDisplay };
    };
    return WithBindingHandler;
}(ConditionalBindingHandler));

/**
 * The `else` binding
 * (not to be mistaken for `<!-- else -->` inside if bindings.
 */
var ElseBindingHandler = /** @class */ (function (_super) {
    __extends(ElseBindingHandler, _super);
    function ElseBindingHandler() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    ElseBindingHandler.prototype.shouldDisplayIf = function () {
        return _super.prototype.shouldDisplayIf.call(this) || this.value === undefined;
    };
    Object.defineProperty(ElseBindingHandler.prototype, "elseChainIsAlreadySatisfied", {
        /**
         * Return any conditional that precedes the given node.
         * @return {object}      { elseChainSatisfied: observable }
         */
        get: function () {
            if (!this._elseChain) {
                this._elseChain = this.readElseChain();
            }
            return unwrap(this._elseChain.elseChainSatisfied);
        },
        enumerable: true,
        configurable: true
    });
    ElseBindingHandler.prototype.readElseChain = function () {
        var node = this.$element;
        do {
            node = node.previousSibling;
        } while (node && node.nodeType !== 1 && node.nodeType !== 8);
        if (!node) {
            return false;
        }
        if (node.nodeType === 8) {
            node = virtualElements.previousSibling(node);
        }
        return domData.get(node, 'conditional') || {};
    };
    return ElseBindingHandler;
}(IfBindingHandler));

var bindings = {
    'if': IfBindingHandler,
    'with': WithBindingHandler,
    ifnot: UnlessBindingHandler,
    unless: UnlessBindingHandler,
    'else': ElseBindingHandler,
    'elseif': ElseBindingHandler
};

export { bindings };
//# sourceMappingURL=binding.if.js.map
